import { createI18n } from "vue-i18n";
import { WorkspaceLocale } from "@/constants/workplaces";
import en from "@/translations/en.json";
import {
  de as vuetifyDeLocale,
  en as vuetifyEnLocale,
  es as vuetifyEsLocale,
  fr as vuetifyFrLocale,
  it as vuetifyItLocale,
  pt as vuetifyPtLocale,
} from "vuetify/locale";

export type MessageSchema = typeof en;

type LazyLocale = Exclude<WorkspaceLocale, WorkspaceLocale.EN_US | WorkspaceLocale.EN_GB>;

/**
 * Each translation file is ~500 KB and a session only ever needs one of them, so only English — the
 * fallback, needed before anything can render — is bundled; the others are fetched when selected.
 * Vuetify's own messages stay static: a few KB each is not worth a request.
 */
const lazyLocales: Record<
  LazyLocale,
  { loadTranslations: () => Promise<{ default: Record<string, unknown> }>; vuetifyMessages: object }
> = {
  [WorkspaceLocale.ES]: {
    loadTranslations: () => import("@/translations/es-ES.json"),
    vuetifyMessages: vuetifyEsLocale,
  },
  [WorkspaceLocale.IT]: {
    loadTranslations: () => import("@/translations/it-IT.json"),
    vuetifyMessages: vuetifyItLocale,
  },
  [WorkspaceLocale.FR]: {
    loadTranslations: () => import("@/translations/fr-FR.json"),
    vuetifyMessages: vuetifyFrLocale,
  },
  [WorkspaceLocale.FR_CA]: {
    loadTranslations: () => import("@/translations/fr-CA.json"),
    vuetifyMessages: vuetifyFrLocale,
  },
  [WorkspaceLocale.DE]: {
    loadTranslations: () => import("@/translations/de-DE.json"),
    vuetifyMessages: vuetifyDeLocale,
  },
  [WorkspaceLocale.PT_BR]: {
    loadTranslations: () => import("@/translations/pt-BR.json"),
    vuetifyMessages: vuetifyPtLocale,
  },
};

const englishMessages = { $vuetify: { ...vuetifyEnLocale }, ...en } as MessageSchema;

export const i18n = createI18n<[MessageSchema], WorkspaceLocale, false>({
  legacy: false, // Vuetify does not support the legacy mode of vue-i18n
  locale: WorkspaceLocale.EN_US,
  fallbackLocale: WorkspaceLocale.EN_US,
  // vue-i18n types `messages` as one entry per locale; the lazy ones are added by `setI18nLocale`.
  messages: {
    [WorkspaceLocale.EN_US]: englishMessages,
    [WorkspaceLocale.EN_GB]: englishMessages,
  } as Record<WorkspaceLocale, MessageSchema>,
});

const pendingLoads = new Map<LazyLocale, Promise<void>>();
let requestedLocale = WorkspaceLocale.EN_US;

/**
 * A failed load is retried on a later request (a dropped connection recovers), but browsers word
 * a chunk deleted by a deploy exactly like a network error, and that one fails forever in this tab.
 * Capping the attempts stops a locale from re-requesting the same missing file — and logging the
 * same error — on every navigation for the rest of the session.
 *
 * Requests within the cooldown of a failure are skipped rather than spending an attempt: they
 * would only hit the same outage, and at boot the initial load, the App watcher and the first
 * navigation all ask within a few seconds (~2.3 s apart on Slow 3G).
 */
const MAX_LOAD_ATTEMPTS = 3;
const RETRY_COOLDOWN_MS = 5_000;
const failedLoads = new Map<LazyLocale, { attempts: number; lastFailedAt: number }>();

const canAttemptLoad = (locale: LazyLocale): boolean => {
  const failed = failedLoads.get(locale);
  if (!failed) return true;
  return (
    failed.attempts < MAX_LOAD_ATTEMPTS && Date.now() - failed.lastFailedAt >= RETRY_COOLDOWN_MS
  );
};

// Own keys only: `in` would also accept inherited names such as "constructor".
const isLazyLocale = (locale: WorkspaceLocale): locale is LazyLocale =>
  Object.prototype.hasOwnProperty.call(lazyLocales, locale);

const loadLocaleMessages = (locale: LazyLocale): Promise<void> => {
  const pendingLoad = pendingLoads.get(locale);
  if (pendingLoad) return pendingLoad;

  const { loadTranslations, vuetifyMessages } = lazyLocales[locale];
  const load = loadTranslations()
    .then(({ default: translations }) => {
      const messages: Record<string, unknown> = {
        $vuetify: { ...vuetifyMessages },
        ...translations,
      };
      i18n.global.setLocaleMessage(locale, messages as MessageSchema);
    })
    .catch((error: unknown) => {
      const attempts = (failedLoads.get(locale)?.attempts ?? 0) + 1;
      failedLoads.set(locale, { attempts, lastFailedAt: Date.now() });
      throw error;
    })
    // Dropped on failure too, so the next request retries instead of reusing the rejection.
    .finally(() => pendingLoads.delete(locale));
  pendingLoads.set(locale, load);
  return load;
};

/**
 * Switches the UI language, fetching its messages first when they are not bundled.
 *
 * Applies synchronously when the messages are already loaded, so a locale preloaded before mount is
 * in place for the first render. Requests can overlap (a fast language switch while a chunk is
 * downloading); only the most recent one is applied.
 *
 * @param locale - The locale to switch to.
 * @returns Whether the locale was applied: false when a newer request superseded this one or its
 * messages failed to load (or are not retried yet — see `RETRY_COOLDOWN_MS` and
 * `MAX_LOAD_ATTEMPTS`), in which case the current locale stays.
 */
export async function setI18nLocale(locale: WorkspaceLocale): Promise<boolean> {
  requestedLocale = locale;
  if (!i18n.global.availableLocales.includes(locale) && isLazyLocale(locale)) {
    if (!canAttemptLoad(locale)) return false;
    try {
      await loadLocaleMessages(locale);
    } catch (error) {
      console.error(`[i18n] Failed to load the ${locale} messages`, error);
      return false;
    }
    if (requestedLocale !== locale) return false;
  }
  i18n.global.locale.value = locale;
  return true;
}
